import { Component, computed, inject, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RestrictedElementDirective } from '@auth';
import { scrollToElement, utils } from '@core';
import { DevicesActions, formatIncidentRuleDuration, IncidentRuleCommon } from '@devices';
import { Store } from '@ngrx/store';
import { AttachmentPosition, isSpaceplanTabType } from '@space-plan/models';
import { ConfirmationDialogComponent } from '@ui';
import { filter } from 'rxjs';

@Component({
  imports: [
    RestrictedElementDirective,
  ],
  selector: 'avs-device-incident-rules-list',
  standalone: true,
  templateUrl: './device-incident-rules-list.component.html',
})
export class DeviceIncidentRulesListComponent {
  activeAttachmentId = input<string | null>(null);
  deviceId = input.required<string>();
  locationId = input.required<string>();
  documentTab = input<string>('new');
  ruleList = input.required<IncidentRuleCommon[]>();
  attachmentSelected = output<string>();
  protected readonly defaultDocumentTab = 'new';
  protected displayRules = computed(() =>
    this.ruleList().map(rule => ({ ...rule, durationLabel: formatIncidentRuleDuration(rule.pendingPeriodSeconds) })),
  );
  private store = inject(Store);
  private dialog = inject(MatDialog);

  protected hasCoordinates(rule: IncidentRuleCommon) {
    const position = utils.parseJson<AttachmentPosition>(rule.unstructuredDataReference);
    const tab = this.documentTab();

    return Boolean(
      tab === 'new'
        ? false
        : isSpaceplanTabType(tab)
          ? position?.['coordinates']?.[tab]
          : false,
    );
  }

  protected assignRule(id: string) {
    this.attachmentSelected.emit(id);

    scrollToElement('avs-spaceplan-tabs');
  }

  protected unassignRule(rule: IncidentRuleCommon) {
    ConfirmationDialogComponent.open(this.dialog, {
      description:
        `Are you sure you want to unassign an incident rule [${rule.code} ${rule.operator} ${rule.conditionValue}] ` +
        'from the block diagram?',
      title: 'Unassign an Incident Rule',
    })
      .pipe(filter(confirmation => confirmation))
      .subscribe(() => this.performUnassign(rule));
  }

  private performUnassign(rule: IncidentRuleCommon) {
    const position = utils.parseJson<AttachmentPosition>(rule.unstructuredDataReference);
    const tab = this.documentTab();

    if (position?.coordinates && isSpaceplanTabType(tab)) {
      delete position.coordinates[tab];
    }

    this.store.dispatch(
      DevicesActions.updateDeviceIncidentRule({
        body: { unstructuredDataReference: JSON.stringify(position ?? { coordinates: {} }) },
        deviceId: this.deviceId(),
        incidentRuleId: rule.id,
        locationId: this.locationId(),
      }),
    );
  }
}
